<template>
  <transition :name="animation">
    <dialog v-if="isActive" ref="dialogRef" class="modal" :class="[size]">
      <div class="modal-backdrop" @click="cancel('outside')"></div>
      <div class="modal-box" :style="{ maxWidth: newWidth }">
        <h3 v-if="title" class="font-bold text-lg">
          {{ title }}
        </h3>

        <div :class="{ 'mt-4': title }">
          <div v-html="message" />

          <div v-if="hasInput" class="form-control w-full mt-2">
            <input ref="input" v-model="prompt" class="input input-bordered w-full" :class="{ 'input-error': validationMessage }" v-bind="inputAttrs" @keyup.enter="confirm">
            <div v-if="validationMessage" class="label">
              <span class="label-text-alt text-error">{{ validationMessage }}</span>
            </div>
          </div>
        </div>

        <div class="modal-action">
          <button v-if="showCancel" ref="cancelButton" class="btn" @click="cancel('button')">
            {{ cancelText }}
          </button>
          <button ref="confirmButton" class="btn" :class="{
            'btn-primary': confirmColor === 'primary',
            'btn-secondary': confirmColor === 'secondary',
            'btn-success': confirmColor === 'success',
            'btn-warning': confirmColor === 'warning',
            'btn-error': confirmColor === 'error',
          }" 
          @click="confirm">
            {{ confirmText }}
          </button>
        </div>
      </div>
    </dialog>
  </transition>
</template>

<script>
import { ref, computed, watch, onMounted, nextTick } from 'vue';

// Helper function to remove element from DOM
const removeElement = (el) => {
  if (el && el.parentNode) {
    el.parentNode.removeChild(el);
  }
};

/**
* @displayName DDialog
*/
export default {
  name: 'DDialog',
  props: {
    title: String,
    message: String,    
    confirmColor: {
      type: String,
      default: 'primary',
    },
    size: String,
    confirmText: {
      type: String,
      default: 'OK',
    },
    optionalInput: Boolean,
    cancelText: {
      type: String,
      default: 'Cancel',
    },
    hasInput: Boolean, // Used internally to know if it's prompt
    inputAttrs: {
      type: Object,
      default: () => ({}),
    },
    onConfirm: {
      type: Function,
      default: () => {},
    },
    focusOn: {
      type: String,
      default: 'confirm',
    },
    width: {
      type: [String, Number],
      default: 960,
    },
  animation: {
      type: String,
      default: 'fade',
    },
    canCancel: {
      type: [Array, Boolean],
      default: () => ['escape', 'x', 'outside', 'button'],
    },
    programmatic: Boolean,
    active: Boolean,
    onCancel: {
      type: Function,
      default: () => {},
    },
  },
  emits: ['close', 'update:active'],
  setup(props, { emit, expose }) {
    const isActive = ref(props.active || false);
    const dialogRef = ref(null);
    const input = ref(null);
    const cancelButton = ref(null);
    const confirmButton = ref(null);
    const validationMessage = ref('');
    const prompt = ref(props.hasInput ? props.inputAttrs.value || '' : '');
    const newWidth = ref(typeof props.width === 'number' ? `${props.width}px` : props.width);

    const cancelOptions = computed(() => {
      return typeof props.canCancel === 'boolean'
        ? props.canCancel
          ? ['escape', 'x', 'outside', 'button']
          : []
        : props.canCancel;
    });

    const showCancel = computed(() => {
      return cancelOptions.value.indexOf('button') >= 0;
    });

    // Watch for active prop changes
    watch(() => props.active, (value) => {
      isActive.value = value;
      
      if (value && dialogRef.value) {
        nextTick(() => {
          dialogRef.value.showModal();
        });
      } else if (!value && dialogRef.value && dialogRef.value.open) {
        dialogRef.value.close();
      }
    });

    // Handle dialog keypress events
    const keyPress = (event) => {
      if (event.key === 'Escape') {
        cancel('escape');
      }
    };

    const cancel = (method) => {
      if (!cancelOptions.value.includes(method)) return;
      props.onCancel(method);
      close();
    };

    /**
     * If it's a prompt Dialog, validate the input.
     * Call the onConfirm prop (function) and close the Dialog.
     */
    const confirm = () => {
      if (input.value && !props.optionalInput) {
        if (!input.value.checkValidity()) {
          validationMessage.value = input.value.validationMessage;
          nextTick(() => input.value.select());
          return;
        }
      }
      
      props.onConfirm(prompt.value);
      close();
    };

    /**
     * Close the Dialog.
     */
    const close = () => {
      isActive.value = false;
      emit('close');
      emit('update:active', false);
      
      // Close the dialog element if it's open
      if (dialogRef.value && dialogRef.value.open) {
        dialogRef.value.close();
      }
      
      // Timeout for the animation complete before destroying if programmatic
      if (props.programmatic) {
        setTimeout(() => {
          // In Vue 3, parent container will handle removal
          dialogRef.value && dialogRef.value.parentNode && removeElement(dialogRef.value);
        }, 150);
      }
    };    onMounted(() => {
      isActive.value = true;
      
      // Set required flag on input if not explicitly defined
      if (props.hasInput && typeof props.inputAttrs.required === 'undefined') {
        input.value && input.value.setAttribute('required', 'required');
      }
      
      nextTick(() => {
        // Set focus to the appropriate element
        if (props.hasInput) {
          input.value && input.value.focus();
        } else if (props.focusOn === 'cancel' && showCancel.value) {
          cancelButton.value && cancelButton.value.focus();
        } else {
          confirmButton.value && confirmButton.value.focus();
        }

        // If the dialog is active, show it
        if (isActive.value && dialogRef.value) {
          dialogRef.value.showModal();
        }
      });
      
      // Add event listeners
      document.addEventListener('keydown', keyPress);
    });

    // Expose methods for programmatic usage
    expose({
      close
    });

    return {
      isActive,
      dialogRef,
      input,
      cancelButton,
      confirmButton,
      validationMessage,
      prompt,
      newWidth,
      showCancel,
      cancelOptions,
      cancel,
      confirm,
      close
    };
  }
};
</script>

<style>
/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease-in-out;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.15s ease-in-out, opacity 0.15s ease-in-out;
}
.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.scale-enter-active,
.scale-leave-active {
  transition: transform 0.15s ease-in-out, opacity 0.15s ease-in-out;
}
.scale-enter-from,
.scale-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>